import { TransferDirectionEnum } from "../../transfers/enums/TransferDirectionEnum";
import { TransportEnum } from "../../transfers/enums/TransferTransportEnum";
import { WizardSaveInput } from "../types/WizardSaveInput";

/** Fields that indicate the user started filling the optional arrival section */
const ARRIVAL_PRESENCE_FIELDS: (keyof WizardSaveInput)[] = [
  "arrivalTransport",
  "arrivalTransportDate",
  "arrivalTransportTime",
  "arrivalNumberOfPassengers",
  "arrivalTransportReference",
  "arrivalTransportReferenceNumber",
  "arrivalLocationFrom",
  "arrivalLocationFromDetails",
  "arrivalLocationTo",
  "arrivalLocationToDetails",
  "arrivalVehicle",
];

/** Fields that indicate the user started filling the optional departure section */
const DEPARTURE_PRESENCE_FIELDS: (keyof WizardSaveInput)[] = [
  "departureTransport",
  "departureTransportDate",
  "departureTransportTime",
  "departureNumberOfPassengers",
  "departureTransportReference",
  "departureTransportReferenceNumber",
  "departureLocationFrom",
  "departureLocationFromDetails",
  "departureLocationTo",
  "departureLocationToDetails",
  "departureVehicle",
];

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "";
}

function sectionHasAnyValue(values: WizardSaveInput, fields: (keyof WizardSaveInput)[]): boolean {
  return fields.some((field) => hasValue(values[field]));
}

function isOtherTransport(transport: unknown): boolean {
  return transport !== undefined && transport !== null && transport !== ""
    && Number(transport) === TransportEnum.OTHER;
}

function hasTransportSelected(transport: unknown): boolean {
  return hasValue(transport);
}

/**
 * True when the user has started filling the optional transfer section shown in step 5
 * (the opposite direction of the package-included transfer from step 4).
 */
export function hasAnyOptionalTransferValue(
  values: WizardSaveInput,
  direction?: TransferDirectionEnum
): boolean {
  if (direction !== TransferDirectionEnum.ARRIVAL && sectionHasAnyValue(values, ARRIVAL_PRESENCE_FIELDS)) {
    return true;
  }

  if (direction !== TransferDirectionEnum.DEPARTURE && sectionHasAnyValue(values, DEPARTURE_PRESENCE_FIELDS)) {
    return true;
  }

  return false;
}

/**
 * All optional-step field names for the section(s) shown in step 5.
 * Used to unregister when the user skips the step empty.
 */
export function getOptionalTransferFieldsToUnregister(
  direction?: TransferDirectionEnum
): (keyof WizardSaveInput)[] {
  const fields: (keyof WizardSaveInput)[] = [];

  if (direction !== TransferDirectionEnum.ARRIVAL) {
    fields.push(...ARRIVAL_PRESENCE_FIELDS);
  }

  if (direction !== TransferDirectionEnum.DEPARTURE) {
    fields.push(...DEPARTURE_PRESENCE_FIELDS);
  }

  return fields;
}

/**
 * Fields to trigger() when the user started filling the optional transfer.
 * Excludes inactive fields (e.g. references/vehicle when transport is OTHER)
 * so unregistered Controllers are not treated as a false "valid".
 */
export function getOptionalTransferFieldsToValidate(
  values: WizardSaveInput,
  direction?: TransferDirectionEnum
): (keyof WizardSaveInput)[] {
  const fields: (keyof WizardSaveInput)[] = [];

  if (direction !== TransferDirectionEnum.ARRIVAL) {
    fields.push(
      "arrivalTransport",
      "arrivalTransportDate",
      "arrivalTransportTime",
      "arrivalNumberOfPassengers",
      "arrivalLocationFrom",
      "arrivalLocationTo",
      "arrivalLocationFromDetails",
      "arrivalLocationToDetails",
    );

    if (hasTransportSelected(values.arrivalTransport) && !isOtherTransport(values.arrivalTransport)) {
      fields.push(
        "arrivalTransportReference",
        "arrivalTransportReferenceNumber",
        "arrivalVehicle",
      );
    }
  }

  if (direction !== TransferDirectionEnum.DEPARTURE) {
    fields.push(
      "departureTransport",
      "departureTransportDate",
      "departureTransportTime",
      "departureNumberOfPassengers",
      "departureLocationFrom",
      "departureLocationTo",
      "departureLocationFromDetails",
      "departureLocationToDetails",
    );

    if (hasTransportSelected(values.departureTransport) && !isOtherTransport(values.departureTransport)) {
      fields.push(
        "departureTransportReference",
        "departureTransportReferenceNumber",
        "departureVehicle",
      );
    }
  }

  return fields;
}
